import React, { useState, useEffect } from 'react';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';
import { Button } from '../../../components/ui/Button';
import Toast from '../../../components/ui/Toast';
import { useToast } from '../../../hooks/useToast';

export default function AdminBarbers() {
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [deleteContext, setDeleteContext] = useState(null);
  
  const { toast, showToast, hideToast } = useToast();

  useEffect(() => {
    const fetchBarbers = async () => {
      const { data, error } = await supabase
        .from('profiles_1740423033529')
        .select('*')
        .eq('role', 'barber')
        .order('created_at', { ascending: false });
      
      if (!error) setBarbers(data || []);
      setLoading(false);
    };

    fetchBarbers();
  }, []);

  const toggleBarberStatus = async (barber) => {
    const newStatus = !barber.is_available;
    const { error } = await supabase.from('profiles_1740423033529').update({ is_available: newStatus }).eq('id', barber.id);
    
    if (!error) {
      setBarbers(barbers.map(b => b.id === barber.id ? { ...b, is_available: newStatus } : b));
      setSelectedBarber({ ...barber, is_available: newStatus });
      showToast(`Barber status updated.`, 'success');
    } else {
      showToast("Error updating status: " + error.message, 'error');
    }
  };

  const executeDelete = async (id) => {
    const { error } = await supabase.rpc('delete_user_completely', { target_user_id: id });
    if (error) {
      showToast("Failed to delete barber: " + error.message, 'error');
    } else {
      setBarbers(barbers.filter(b => b.id !== id));
      setSelectedBarber(null);
      showToast("Barber deleted successfully.", 'success');
    }
    setDeleteContext(null);
  };

  return (
    <div className="animate-in fade-in duration-500 relative">
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}

      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-black text-brand-dark tracking-tight">Barber Management</h1>
          <p className="text-gray-500 font-medium italic mt-1">Oversee the professional network.</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400 font-bold uppercase text-[10px] tracking-widest">Loading barbers...</div>
        ) : barbers.length === 0 ? (
          <div className="p-12 text-center text-gray-400 font-medium italic">No barbers registered yet.</div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50 text-gray-400 text-[10px] uppercase tracking-widest font-black">
                <th className="px-8 py-5">Barber</th>
                <th className="px-8 py-5">Status</th>
                <th className="px-8 py-5">Rating</th>
                <th className="px-8 py-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {barbers.map((barber) => (
                <tr key={barber.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-4">
                      <img src={barber.avatar_url || `https://ui-avatars.com/api/?name=${barber.full_name}&background=0047FF&color=fff`} className="w-10 h-10 rounded-full object-cover" alt={barber.full_name} />
                      <div>
                        <p className="font-bold text-brand-black">{barber.full_name}</p>
                        <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Joined: {new Date(barber.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase border ${barber.is_available ? 'bg-green-50 text-green-700 border-green-100' : 'bg-red-50 text-red-700 border-red-100'}`}>
                      {barber.is_available ? 'Active/Online' : 'Offline/Suspended'}
                    </span>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-1">
                      <SafeIcon name="Star" className="text-brand-accent fill-current text-sm" />
                      <span className="text-sm font-black text-brand-dark">{barber.rating || '5.0'}</span>
                    </div>
                  </td>
                  <td className="px-8 py-5 text-right">
                    <button onClick={() => setSelectedBarber(barber)} className="text-brand-primary font-bold text-xs hover:underline uppercase tracking-wider">Manage Profile</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Barber Profile Modal */}
      {selectedBarber && (
        <div className="fixed inset-0 bg-brand-dark/60 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-[2.5rem] w-full max-w-md p-10 shadow-2xl animate-in zoom-in duration-300">
            <div className="text-center mb-8">
              <img src={selectedBarber.avatar_url || `https://ui-avatars.com/api/?name=${selectedBarber.full_name}&background=0047FF&color=fff`} className="w-24 h-24 rounded-full mx-auto object-cover border-4 border-gray-50 mb-4" alt="" />
              <h2 className="text-2xl font-black text-brand-dark">{selectedBarber.full_name}</h2>
              <p className="text-brand-primary font-black text-[10px] uppercase tracking-widest mt-1">Professional Barber</p>
            </div>
            
            <div className="space-y-4 bg-gray-50 p-6 rounded-2xl border border-gray-100 mb-8">
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Email Address</p>
                <p className="font-bold text-brand-dark">{selectedBarber.email}</p>
              </div>
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Bio</p>
                <p className="font-bold text-brand-dark text-sm">{selectedBarber.bio || 'No bio provided'}</p>
              </div>
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</p>
                <p className={`font-black uppercase text-xs ${selectedBarber.is_available ? 'text-green-600' : 'text-red-600'}`}>
                  {selectedBarber.is_available ? 'Active Performer' : 'Offline / Suspended'}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant={selectedBarber.is_available ? "secondary" : "primary"} className="flex-[1.5] text-xs px-2" onClick={() => toggleBarberStatus(selectedBarber)}>
                {selectedBarber.is_available ? 'Suspend' : 'Reactivate'}
              </Button>
              <Button variant="danger" className="flex-[1.5] text-xs px-2 bg-red-600 hover:bg-red-700 text-white" onClick={() => setDeleteContext(selectedBarber)}>
                Delete
              </Button>
              <Button variant="outline" className="flex-1 text-xs px-2" onClick={() => setSelectedBarber(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Barber Modal */}
      {deleteContext && (
        <div className="fixed inset-0 bg-brand-dark/60 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-[2.5rem] w-full max-w-sm p-8 shadow-2xl text-center">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon name="AlertTriangle" className="text-3xl" />
            </div>
            <h3 className="text-xl font-black text-brand-dark mb-2">Delete Barber?</h3>
            <p className="text-gray-500 text-sm mb-6">Are you sure you want to completely delete <strong>{deleteContext.full_name}</strong>? This action cannot be undone.</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteContext(null)}>Cancel</Button>
              <Button variant="danger" className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={() => executeDelete(deleteContext.id)}>Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}